/*
 * Copyright 2018 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

export default class AppBase extends THREE.EventDispatcher {
  constructor(props) {
    super(props);
    this.enableAR = this.enableAR.bind(this);
    if (navigator.xr &&
        navigator.xr.requestDevice &&
        window.XRSession.prototype.requestHitTest &&
        // Check that requestFrameOfReference exists to confirm
        // it's the Chrome m70-m72 WebXR API version.
        window.XRSession.prototype.requestFrameOfReference) {
      navigator.xr
        .requestDevice()
        .then(device => {
          this.device = device;
          console.log('found xr device');
        })
        .catch(() => {
          console.log('unable to get xr device');
        });
    } else {
      console.log('xr device not detected!');
    }
  }

  _setup = props => {
    this._setupProperties(props);
    this._setupRenderer(props);
    this._setupCamera();
    this._setupEvents();
    this._setupStats(props);
    this.setup(props);
    this._update();
  };

  _setupProperties = props => {
    this.canvas = props.canvas;
    this.container = props.container;
    this.ARMode = false;
    this.lastTime = window.performance.now();
    this.currentTime = window.performance.now();
    this.deltaTime = this.currentTime - this.lastTime;
  };

  _setupRenderer = props => {
    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      canvas: props.canvas,
      preserveDrawingBuffer: true,
    });
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.autoClear = false;
    this.renderer.gammaInput = true;
    this.renderer.gammaOutput = true;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    // this.renderer.setClearColor(0x000000, 1.0);

    this.canvasRect = this.container.getBoundingClientRect();
    this.renderer.setSize(this.canvasRect.width, this.canvasRect.height);
    this.dispatchResizedEvent();

    window.addEventListener('resize', event => {
      if (!this.IsAR()) {
        this.canvasRect = this.container.getBoundingClientRect();
        this.renderer.setSize(this.canvasRect.width, this.canvasRect.height);
        this.dispatchResizedEvent();
      }
    });

    document.addEventListener('fullscreenchange', event => {
      if (!this.IsAR()) {
        this.canvasRect = this.container.getBoundingClientRect();
        this.renderer.setSize(this.canvasRect.width, this.canvasRect.height);
        this.dispatchResizedEvent();
      }
    });
  };

  _setupStats = props => {
    let statsProps = props.stats != undefined ? props.stats : false;
    this.showStats = props.showStats != undefined ? props.showStats : false;
    let typeMap = {
      fps: 0,
      ms: 1,
      mb: 2,
    };

    if (this.showStats) {
      let type = typeMap[statsProps.type];
      this.stats = new Stats();
      this.stats.showPanel(type != undefined ? type : 0);
      this.stats.dom.style.position = 'relative';
      this.container.appendChild(this.stats.dom);
      this.stats.dom.style.zIndex = 10000;
    }
  };

  // Override these
  setup = props => {};
  update = (time, delta) => {};
  render = () => {};

  _bindSessionFbo = () => {
    if (this.xrSession) {
      const gl = this.renderer.getContext();
      gl.bindFramebuffer(gl.FRAMEBUFFER, this.xrSession.baseLayer.framebuffer);
    }
  };

  _unbindSessionFbo = () => {
    const gl = this.renderer.getContext();
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  };

  _update = (time, frame) => {
    if (this.stats) {
      this.stats.begin();
    }

    this._updateTime();

    if (this.IsAR()) {
      this._updateRenderer(frame);
      this._updateCamera(frame);
      this._bindSessionFbo();
      this.render(this.camera, frame);
    } else {
      this._unbindSessionFbo();
      this.render();
    }

    if (this.stats) {
      this.stats.end();
    }

    if (this.xrSession && this.IsAR()) {
      this.xrSession.requestAnimationFrame(this._update);
    } else {
      requestAnimationFrame(this._update);
    }
  };

  _updateTime = () => {
    this.currentTime = window.performance.now();
    this.deltaTime = this.currentTime - this.lastTime;
    this.update(this.currentTime, this.deltaTime);
    this.lastTime = this.currentTime;
  };

  _updateCamera = frame => {
    if (frame) {
      let pose = frame.getDevicePose(this.frameOfRef);
      if (pose) {
        const view = frame.views[0];
        this.camera.projectionMatrix.fromArray(view.projectionMatrix);
        this.camera.matrix.fromArray(pose.getViewMatrix(view));
        this.camera.matrix.getInverse(this.camera.matrix);
        this.camera.updateMatrixWorld(true);
        this.camera.matrix.decompose(
          this.camera.position,
          this.camera.quaternion,
          this.camera.scale
        );
      }
    }
  };

  _updateRenderer = frame => {
    if (frame) {
      const view = frame.views[0];
      const viewport = this.xrSession.baseLayer.getViewport(view);
      const size = this.renderer.getSize();
      this.renderer.setViewport(0, 0, viewport.width, viewport.height);
      this.renderer.setSize(viewport.width, viewport.height);
      if (size.width != viewport.width && size.height != viewport.height) {
        this.dispatchResizedEvent();
      }
    }
  };

  dispatchResizedEvent = () => {
    this.dispatchEvent({
      type: 'resized',
      object: this.renderer.getSize(),
    });
  };

  dispatchExitedAREvent = () => {
    this.dispatchEvent({
      type: 'exitedAR',
      object: this,
    });
  };

  dispatchEnteredAREvent = () => {
    this.dispatchEvent({
      type: 'enteredAR',
      object: this,
    });
  };

  dispatchFullscreenEvent = () => {
    let fs = document.webkitIsFullScreen;
    if (fs) {
      this.dispatchEvent({
        type: 'enteredFullscreen',
        object: fs,
      });
    } else {
      this.dispatchEvent({
        type: 'exitedFullscreen',
        object: fs,
      });
    }
  };

  _setupCamera = () => {
    let size = this.renderer.getDrawingBufferSize();
    this.camera = new THREE.PerspectiveCamera(
      60,
      size.width / size.height,
      0.01,
      100
    );
    this.camera.matrixAutoUpdate = false;
  };

  _setupEvents = () => {
    let fsEvent = event => {
      this.dispatchFullscreenEvent();
    };
    document.addEventListener('fullscreenchange', fsEvent);
    document.addEventListener('webkitfullscreenchange', fsEvent);
    document.addEventListener('mozfullscreenchange', fsEvent);
  };

  async setupXRSession() {
    this.xrcanvas = document.createElement('canvas');
    this.xrcanvas.setAttribute('id', 'xr-canvas');
    const ctx = this.xrcanvas.getContext('xrpresent');
    const session = await this.device.requestSession({
      outputContext: ctx,
      environmentIntegration: true,
    });
    const frameOfRef = await session.requestFrameOfReference('eye-level');

    const gl = this.renderer.getContext();
    await gl.setCompatibleXRDevice(this.device);
    session.baseLayer = new XRWebGLLayer(session, gl);

    this.xrSession = session;
    this.frameOfRef = frameOfRef;
  }

  async enableAR() {
    const hasEnteredFS = this._onNextFullscreen();
    this.enterFullscreen();

    if (!this.IsAR()) {
      if (this.device && !this.xrSession) {
        try {
          await this.setupXRSession();

          // If we're not fullscreen upon successful session creation,
          // it might be because we just installed ARCore and jumped
          // back to Chrome, losing fullscreen. Abort AR and allow
          // the user to try again.
          // We also await on the initial entering FS event so that
          // we don't depend on the FS happening legitimately before
          // session resolution
          await hasEnteredFS;

          if (!document.webkitIsFullScreen) {
            // Call setAR(false) here to shut down the newly
            // created XRSession, since we lost fullscreen
            this.setAR(false);
            return;
          }

          this.ARModeRequested = true;
          this.dispatchEvent({ type: 'enableAR', object: this });
        } catch (e) {
          // If we get a rejection (could be from rejecting the camera permissions,
          // rejecting the ARCore install prompt)
          // This could possibly trigger between full screen request,
          // and before it's activated due to race condition, but if the session
          // is rejected, it's most likely due to a user prompt cancel.
          this.exitFullscreen();
        }
      }
    }
  }

  disableAR = () => {
    if (this.IsAR()) {
      this.ARModeRequested = false;
      this.dispatchEvent({ type: 'disableAR', object: this });
    }
  };

  enterFullscreen = () => {
    if (!document.webkitIsFullScreen) {
      let element = this.container;
      if (element.requestFullScreen) element.requestFullScreen();
      else if (element.webkitRequestFullScreen)
        element.webkitRequestFullScreen();
      else if (element.mozRequestFullScreen) element.mozRequestFullScreen();
    }
  };

  exitFullscreen = () => {
    if (document.webkitIsFullScreen) {
      let element = document;
      if (element.exitFullscreen) element.exitFullscreen();
      else if (element.webkitExitFullscreen) element.webkitExitFullscreen();
      else if (element.mozCancelFullScreen) element.mozCancelFullScreen();
    }
  };

  IsAR = () => {
    return this.ARMode;
  };

  setAR = state => {
    if (state) {
      this.container.appendChild(this.xrcanvas);
      this.renderer.setPixelRatio(1);
    } else {
      if (this.xrSession) {
        // If xrcanvas is not yet in the DOM, just catch the
        // error
        try {
          this.container.removeChild(this.xrcanvas);
        } catch (e) {
        }
      }
      this.container.classList.add('container');
      this.container.classList.remove('fullscreen');
      this.canvasRect = this.container.getBoundingClientRect();
      this.renderer.setPixelRatio(window.devicePixelRatio);
      this.renderer.setSize(this.canvasRect.width, this.canvasRect.height);

      this.xrSession.end(); // Do not wait for resolve
      this.xrSession = null;
      this.frameOfRef = null;

      // Manually queue up a frame on the window RAF; since we kill
      // the XRSession, its callback RAF will never be called, which
      // stops the loop when going back into non-AR.
      // https://github.com/immersive-web/webxr/issues/352
      requestAnimationFrame(this._update);
    }
    this.ARMode = state;
    if (this.ARMode) {
      this.dispatchEnteredAREvent();
    } else {
      this.dispatchExitedAREvent();
    }
    this.dispatchResizedEvent();
  };

  hasAR = () => {
    return this.device ? true : false;
  };

  /**
   * Returns a promise that resolves when the next
   * request for fullscreen is completed.
   */
  _onNextFullscreen() {
    return new Promise((resolve) => {
      const handler = () => {
        if (document.webkitIsFullScreen) {
          this.removeEventListener('enteredFullscreen', handler);
          resolve();
        }
      }
      this.addEventListener('enteredFullscreen', handler);
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/AppBase.js